/**
 * SaveToCaseDialog — « Enregistrer dans le dossier ».
 *
 * Porte de sortie du canvas côté avocat (audit métier 2026-07-30) : le brouillon
 * devient une vraie pièce du dossier, donc visible dans l'arborescence, éligible
 * au bordereau, ouvrable par l'add-in Word et lisible par l'agent.
 *
 * Le contenu est **copié** : le brouillon reste ouvrable dans sa conversation
 * après enregistrement (le backend duplique l'objet S3 sous une clé `cases/…`).
 *
 * ─── Ce qui change pour l'avocat (2026-09-15) ─────────────────────────────
 * Le CHOIX de la destination passe à `SaveToDestinationDialog`, partagé avec les
 * trois autres écrans d'enregistrement. Ce module ne garde que ce qui lui est
 * propre : les gardes du canvas, le point de contrôle du brouillon et l'écriture.
 *
 * Deux manques de l'ancien écran disparaissent au passage : la pièce se rangeait
 * TOUJOURS à la racine de l'onglet (aucun sous-dossier n'était proposé, ni même
 * transmis — cf. `folderPath` ajouté le même jour côté backend), et la
 * sous-catégorie « Partie adverse » se choisissait dans une liste déconnectée de
 * l'arborescence, si bien qu'on pouvait taguer « Écritures » une pièce déposée
 * dans « Pièces adverses ». Le tag se dérive désormais du dossier choisi.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { getCategoryTabLabel } from '@/lib/droit-public';
import { useCase } from '@/hooks/useCase';
import { SaveToDestinationDialog } from '@/components/documents/destination/SaveToDestinationDialog';
import type { DestinationValue } from '@/components/documents/destination/destinationModel';
import {
  canvasDocxTooLargeMessage,
  canvasDocxVerificationUnavailableMessage,
  canvasDraftApi,
  isCanvasDocxFormatError,
  isCanvasDocxSuperseded,
} from '../api/canvasDraft.api';
import { annoncerVersionDepassee } from './annoncerVersionDepassee';
import {
  isCanvasSessionProtectedByWork,
  useCanvasSessionProtectedByWork,
} from '../state/canvasSessionProtection';
import { useCanvasDocumentScope } from '../state/canvasDocumentScopeContext';
import {
  captureCanvasDurableBoundary,
  checkpointMatchesCanvasDurableBoundary,
} from '../state/canvasDurableBoundary';

interface SaveToCaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dossier cible — celui de la conversation courante. */
  caseId: string | null;
  /** Nom pré-rempli (titre affiché dans le header du canvas). */
  defaultName: string;
}

export function SaveToCaseDialog({
  open,
  onOpenChange,
  caseId,
  defaultName,
}: SaveToCaseDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const scope = useCanvasDocumentScope();
  /**
   * Un tour d'agent travaille-t-il sur le document ouvert ? Lu ici plutôt que
   * reçu en prop : la garde doit vivre au point d'écriture, sinon elle vaut
   * seulement pour l'appelant qui a pensé à la passer.
   */
  const documentProtected = useCanvasSessionProtectedByWork(scope.conversationId);
  /**
   * Le dossier est lu pour sa MATIÈRE seule : en droit public l'arborescence
   * intitule « Mes mémoires » ce qu'elle appelle ailleurs « Mes écritures ».
   * Sert ici au seul message de succès — le sélecteur, lui, lit le dossier
   * RÉELLEMENT choisi, qui peut ne pas être celui de la conversation.
   */
  const { data: caseItem } = useCase(caseId ?? undefined);

  const handleConfirm = React.useCallback(
    async ({
      destination,
      name,
      subcategory,
    }: {
      destination: DestinationValue;
      name: string;
      subcategory: string | null;
    }) => {
      // La protection exacte se consulte ICI, au point d'écriture (#855). Le bandeau
      // refuse déjà d'ouvrir ce dialogue pendant une rédaction, mais un dialogue
      // OUVERT AVANT le début du tour survit à sa prise : sans cette garde,
      // l'avocat verserait au dossier un acte figé en pleine réécriture, qui ne
      // se signale pas comme tronqué.
      if (documentProtected || isCanvasSessionProtectedByWork(scope.conversationId)) {
        toast.error(
          'Hector travaille sur ce document. L’enregistrer maintenant produirait '
          + 'un acte incomplet. Réessaie à la fin de sa réponse.',
        );
        throw new Error('canvas protégé par un tour en cours');
      }
      const controller = scope.getDraftController();
      const boundary = captureCanvasDurableBoundary({
        mountEpoch: scope.getSnapshot().mountEpoch,
        draft: controller.getSnapshot(),
      });
      if (!boundary) {
        toast.error(
          'Aucun brouillon enregistré pour ce canvas. Saisis ou génère du contenu, puis réessaie.',
        );
        throw new Error('aucun brouillon à enregistrer');
      }

      try {
        // La copie vers le dossier lit le brouillon SERVEUR. Attendre le point de
        // contrôle exact évite de copier la version du debounce précédent.
        const checkpoint = await controller.checkpoint();
        if (!checkpointMatchesCanvasDurableBoundary(boundary, {
          ...checkpoint,
          liveMountEpoch: scope.getSnapshot().mountEpoch,
          liveDraft: controller.getSnapshot(),
        })) {
          throw new Error('Le document a changé pendant sa sauvegarde. Vérifie la version affichée puis recommence l’enregistrement.');
        }
        if (isCanvasSessionProtectedByWork(scope.conversationId)) {
          toast.error(
            'Hector travaille sur ce document. Attendez la fin de sa réponse avant de l’enregistrer dans le dossier.',
          );
          throw new Error('canvas protégé par un tour en cours');
        }

        const saved = await canvasDraftApi.saveToCase(checkpoint.draftId, {
          caseId: destination.caseId,
          documentCategory: destination.category,
          documentSubcategory: subcategory,
          documentName: name,
          folderPath: destination.folderPath,
          mode: 'create',
          expectedAttemptId: checkpoint.attemptId,
        });
        // Invalider spécifique ET général (convention du repo) : sinon la pièce
        // n'apparaît qu'au prochain reload complet de l'arborescence.
        await queryClient.invalidateQueries({ queryKey: ['case-documents', destination.caseId] });
        await queryClient.invalidateQueries({ queryKey: ['case-documents'] });
        await queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
        await queryClient.invalidateQueries({ queryKey: ['folder-tree'] });

        // Le libellé d'onglet suit le dossier CHOISI quand c'est celui de la
        // conversation ; ailleurs on n'a pas sa matière sous la main et le
        // catalogue générique suffit.
        const tabLabel = getCategoryTabLabel(
          destination.caseId === caseId ? caseItem : undefined,
          destination.category,
        );
        if (saved.aiProcessing.status === 'held') {
          toast.warning(
            `« ${name} » a été enregistré, mais son traitement IA est en attente : `
            + `ce dossier contient ${saved.aiProcessing.totalSourceDocuments} pièces `
            + `(limite : ${saved.aiProcessing.limit}).`,
          );
        } else {
          toast.success(`« ${name} » enregistré dans ${tabLabel}. Traitement IA lancé.`);
        }
      } catch (err: unknown) {
        console.error('[SaveToCaseDialog] échec enregistrement dans le dossier', err);
        if (isCanvasDocxSuperseded(err)) {
          annoncerVersionDepassee('Enregistrement impossible');
          throw err;
        }
        const message = err instanceof Error && err.message.startsWith('Le document a changé pendant sa sauvegarde.')
          ? err.message
          // #1743 — « réessayer seulement » : le checkpoint a échoué, donc rien
          // n'est rangé ; ranger la version vérifiée précédente copierait autre
          // chose que ce que l'avocat voit.
          : canvasDocxVerificationUnavailableMessage(err)
            ?? canvasDocxTooLargeMessage(err)
            ?? (isCanvasDocxFormatError(err)
              ? 'Le document n’a pas pu être enregistré : une correction de format est nécessaire.'
              : 'Le document n’a pas pu être enregistré. Réessayez dans quelques instants.');
        toast.error(message);
        // Relancé : le dialogue partagé garde la fenêtre ouverte, choix intacts.
        throw err;
      }
    },
    [documentProtected, caseId, caseItem, queryClient, scope],
  );

  return (
    <SaveToDestinationDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Enregistrer dans le dossier"
      description="Une copie du document est enregistrée dans l’emplacement choisi."
      confirmLabel="Enregistrer le document"
      nameField={{
        label: 'Nom du document',
        placeholder: 'Conclusions en réplique',
        initialValue: defaultName,
      }}
      initialCaseId={caseId ?? undefined}
      defaultCategory="procedure"
      onConfirm={handleConfirm}
    />
  );
}
